import React, { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { BrioModal, BrioModalBody, BrioButton, BrioInput, BrioSelect } from '../../components/BrioComponents';
import { ToolbarIcon } from './ToolbarIcon';

// Manage the project's scope/responsibility parties and bulk-assign them to
// lines (all, or a whole trade at once). Per-line tweaks happen in the grid's
// Scope column; this is for setup + bulk moves.
function ScopeModal({ parties, trades, onAddParty, onUpdateParty, onDeleteParty, onAssign, onClose }) {
  const [newName, setNewName] = useState('');
  const [newExternal, setNewExternal] = useState(true);
  const [busy, setBusy] = useState(false);
  const [assignParty, setAssignParty] = useState('');
  const [assignTrade, setAssignTrade] = useState('__all__');
  const [msg, setMsg] = useState(null);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    await onAddParty({ name: newName.trim(), isExternal: newExternal });
    setBusy(false);
    setNewName('');
  };

  const handleAssign = async () => {
    setBusy(true);
    const trade = assignTrade === '__all__' ? null : assignTrade;
    const res = await onAssign(assignParty || null, trade);
    setBusy(false);
    if (res?.success) setMsg(`Assigned ${res.count} line${res.count === 1 ? '' : 's'}.`);
    else setMsg(res?.error || 'Assign failed.');
  };

  return (
    <BrioModal title="Scope / responsibility" onClose={onClose} width="md" footer={
      <BrioButton variant="secondary" onClick={onClose}>Done</BrioButton>
    }>
      <BrioModalBody>
        <p style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', marginTop: 0 }}>
          Who is responsible for designing / specifying a scope. Mark a party <strong>external</strong> when
          it's another consultant's or the client's scope (e.g. an MEP package with a services consultant) so
          the Summary can subtotal your scope separately. Unassigned lines count as your scope.
        </p>

        <h4 style={{ margin: '0.5rem 0 0.4rem', fontFamily: 'Raleway, sans-serif' }}>Parties</h4>
        {parties.length === 0 && <div style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', marginBottom: '0.4rem' }}>None yet — add one below.</div>}
        {parties.map((p) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            <BrioInput defaultValue={p.name} onBlur={(e) => e.target.value.trim() && e.target.value !== p.name && onUpdateParty(p.id, { name: e.target.value })} style={{ flex: 1 }} />
            <label style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={p.isExternal} onChange={(e) => onUpdateParty(p.id, { isExternal: e.target.checked })} /> External
            </label>
            <ToolbarIcon onClick={() => onDeleteParty(p.id)} danger tooltip="Delete party (lines revert to unassigned)">
              <Trash2 size={15} />
            </ToolbarIcon>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
          <BrioInput value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. XYZ MEP Consultant"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }} style={{ flex: 1 }} />
          <label style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={newExternal} onChange={(e) => setNewExternal(e.target.checked)} /> External
          </label>
          <BrioButton variant="secondary" onClick={handleAdd} disabled={busy || !newName.trim()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
            <Plus size={14} /> Add
          </BrioButton>
        </div>

        <h4 style={{ margin: '1.1rem 0 0.4rem', fontFamily: 'Raleway, sans-serif' }}>Bulk-assign</h4>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.6rem', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
            Set scope
            <BrioSelect value={assignParty} onChange={(e) => setAssignParty(e.target.value)} style={{ display: 'block', marginTop: '0.2rem', minWidth: 180 }}>
              <option value="">Unassigned (our scope)</option>
              {parties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.isExternal ? ' (external)' : ''}</option>)}
            </BrioSelect>
          </label>
          <label style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
            For
            <BrioSelect value={assignTrade} onChange={(e) => setAssignTrade(e.target.value)} style={{ display: 'block', marginTop: '0.2rem', minWidth: 160 }}>
              <option value="__all__">All lines</option>
              {trades.map((t) => <option key={t.code} value={t.code}>Trade {t.code} ({t.count})</option>)}
            </BrioSelect>
          </label>
          <BrioButton variant="primary" onClick={handleAssign} disabled={busy}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.82rem' }}>
            Apply
          </BrioButton>
        </div>
        {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: 'var(--brio-text-muted)' }}>{msg}</div>}
      </BrioModalBody>
    </BrioModal>
  );
}

export default ScopeModal;
